import React from 'react';
import Loader from 'src/shared/components/loader/Loader';
import PageNotFound from 'src/shared/components/page-not-found/PageNotFound';
import { useProductAccess } from 'src/shared/hooks/useProductLicensing';
import { ProductKey } from 'src/shared/types/engine/products';
import { ProductAccess } from 'src/shared/types/productLicensing';
import { GatedProductContext } from './GatedProductContext';

type ProductGateProps = {
    product: ProductKey;
    /** Whether there are results to show. `undefined` while the page still checks. */
    hasResults: boolean | undefined;
    /** The page a pitched user sees. Without it, a pitched user gets page-not-found. */
    upsell?: React.ReactNode;
    /** The results. They render, and their hooks run, only for a user who may see them. */
    children: React.ReactNode;
};

/**
 * Shows a product page only to a user who may see it, and the right
 * substitute to everyone else: a loader until access is known, page-not-found
 * when the product is hidden, and the upsell page when it is pitched.
 */
const ProductGate = ({ product, hasResults, upsell, children }: ProductGateProps) => {
    const access = useProductAccess(product, hasResults);

    switch (access) {
        case ProductAccess.LOADING:
            return <Loader />;
        case ProductAccess.HIDDEN:
            return <PageNotFound />;
        case ProductAccess.DEFAULT:
            return upsell ? <>{upsell}</> : <PageNotFound />;
        case ProductAccess.LICENSED:
            return <GatedProductContext.Provider value={product}>{children}</GatedProductContext.Provider>;
    }
};

export default ProductGate;
